// src/form-component/FormInputDropdown.tsx

import { Checkbox, FormControl, InputLabel, ListItemText, ListSubheader, MenuItem, OutlinedInput, Select, SelectChangeEvent } from '@mui/material';
import { ReactNode, useCallback, useState, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Control, Controller } from 'react-hook-form';

// Add styling for popover menu paper
const StyledPopoverStyles = `
  & .MuiPaper-root.MuiPopover-paper.MuiMenu-paper {
    box-shadow: 0px 12px 12px 0px rgba(34, 65, 102, 0.09) !important;
    border: 1px solid #DFDFDF;
  }
`;

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  paddingLeft: '0!important',
  [theme.breakpoints.down(1520)]: {
    padding: '0 9px 0 0!important',
  },
}));

interface IOption {
  isLabel?: boolean;
  value: any;
  label: ReactNode;
}

interface IProps {
  id?: string;
  name: string;
  multiple?: boolean;
  disabled?: boolean;
  label: ReactNode;
  options: IOption[];
  defaultValue?: any;
  control: Control<any>;
  onChangeCustom?: (e: SelectChangeEvent<string | string[]>) => void;
  onOpenSelect?: () => void;
  withApplyButton?: boolean; // Add Apply button to defer filter application
}

export const MultipleSelectDropdown = ({
  id,
  name,
  control,
  label,
  options,
  defaultValue = null,
  multiple = true,
  onChangeCustom = null,
  onOpenSelect,
  disabled,
  withApplyButton = false,
}: IProps) => {
  // Local state for pending selections when using Apply button
  const [pendingValue, setPendingValue] = useState<string | string[] | null>(null);
  const selectRef = useRef<any>(null);
  const checkValue = useCallback(
    (value?: string | string[]) => {
      if (value) {
        return value;
      }
      if (multiple) {
        return [];
      }
      return '';
    },
    [multiple]
  );

  const ValueRender = useCallback(
    (value: string | string[]) => {
      if (multiple && Array.isArray(value)) {
        const selected = value.map((v) => options?.find((option) => option.value === v)?.label);
        return <span className='capitalize text-14 xl:text-16'>{selected.join(', ')}</span>;
      }
      const selected = options?.find((option) => option.value === value)?.label;
      return <span className='capitalize text-14 xl:text-16'>{selected}</span>;
    },
    [multiple, options]
  );

  return (
    <FormControl size='small'>
      <InputLabel>{label}</InputLabel>
      <Controller
        defaultValue={defaultValue}
        render={({ field: { onChange, value = [] } }) => {
          // Use pending value for display when Apply button is enabled
          const displayValue = withApplyButton && pendingValue !== null ? pendingValue : value;

          return (
            <Select
              ref={selectRef}
              id={id || 'dd'}
              onOpen={() => {
                if (onOpenSelect) onOpenSelect();
                // Initialize pending value with current value when opening
                if (withApplyButton) {
                  setPendingValue(value);
                }
              }}
              onClose={() => {
                // Reset pending value when closing without applying (for Apply button mode)
                if (withApplyButton) {
                  setPendingValue(null);
                }
              }}
              onChange={(event: SelectChangeEvent<string | string[]>) => {
                if (withApplyButton) {
                  // Store in pending state, don't apply yet
                  setPendingValue(event.target.value);
                } else {
                  // Apply immediately (original behavior)
                  onChange(event);
                  if (onChangeCustom) {
                    onChangeCustom(event);
                  }
                }
              }}
              value={checkValue(displayValue)}
              multiple={multiple}
              input={<OutlinedInput label={label} />}
              renderValue={ValueRender}
              disabled={disabled}
              MenuProps={{
                autoFocus: false,
                sx: {
                  '& .MuiPaper-root.MuiPopover-paper.MuiMenu-paper': {
                    boxShadow: '0px 12px 12px 0px rgba(34, 65, 102, 0.09) !important',
                    border: '1px solid #DFDFDF',
                    marginTop: '5px',
                    borderRadius: '8px',
                  },
                },
              }}
            >
              {options?.map((option) => {
                if (option?.isLabel) {
                  return (
                    <ListSubheader
                      key={option.value}
                      sx={(theme) => ({
                        fontSize: '16px',
                        [theme.breakpoints.down(1520)]: {
                          fontSize: '14px',
                          '&.MuiListSubheader-root': {
                            lineHeight: '38px',
                          },
                        },
                      })}
                    >
                      {option.label}
                    </ListSubheader>
                  );
                }
                return (
                  <MenuItem key={option.value} value={option.value}>
                    {multiple && <StyledCheckbox checked={!!displayValue?.find((v) => v === option.value)} />}
                    <ListItemText
                      primary={option.label}
                      sx={{ textTransform: 'capitalize', '& .MuiTypography-root': { fontSize: { xs: '14px', xl: '16px' } } }}
                    />
                  </MenuItem>
                );
              })}

              {/* Apply Button - Only shown when withApplyButton is true */}
              {withApplyButton && (
                <div className='px-16 py-12 border-t border-neutral-20'>
                  <button
                    type='button'
                    onClick={() => {
                      // Apply the pending value
                      if (pendingValue !== null) {
                        onChange(pendingValue);
                        if (onChangeCustom) {
                          onChangeCustom({ target: { value: pendingValue } } as SelectChangeEvent<string | string[]>);
                        }
                      }
                      // Close menu and reset pending state

                      setPendingValue(null);
                    }}
                    className='w-full rounded-[4px] border-2 border-[#00992B] bg-[#00992B] px-16 py-4 text-center text-white font-roboto text-14 font-medium hover:bg-[#008F45] hover:border-[#008F45] transition-colors'
                  >
                    Apply
                  </button>
                </div>
              )}
            </Select>
          );
        }}
        control={control}
        name={name}
      />
    </FormControl>
  );
};
